import React, { useState } from 'react';

// components
import SelfServiceOriginationPointSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/selfService/SelfServiceOriginationPointSelection';
import TransactionTypeSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/TransactionTypeSelection';
import CustomerSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/customerService/CustomerSelection';
import TransactionAmountSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/TransactionAmountSelection';
import DeviceTransactionFinalReviewScreen from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/DeviceTransactionFinalReviewScreen';

const SelfServiceDeviceTransactionSimulator = (props) => {
  const { device, onBack, onSubmit, customers } = props;
  const selfServiceDeviceTransactionScreens = {
    ORIGINATION_SIMULATION: 'ORIGINATION_SIMULATION',
    TRANSACTION_TYPE: 'TRANSACTION_TYPE',
    CUSTOMER_SELECTION: 'CUSTOMER_SELECTION',
    TRANSACTION_AMOUNT: 'TRANSACTION_AMOUNT',
    FINAL_REVIEW: 'FINAL_REVIEW',
  };

  // form data
  const [wizardStep, setWizardStep] = useState(
    selfServiceDeviceTransactionScreens.ORIGINATION_SIMULATION,
  );
  const [selectedOriginationPoint, setSelectedOriginationPoint] = useState(null);
  const [selectedTransactionType, setSelectedTransactionType] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomerDisplay, setSelectedCustomerDisplay] = useState(null);
  const [selectedTransactionAmountInDollars, setSelectedTransactionAmountInDollars] = useState(null);
  const selectedPaymentStatus = 'FULL_CYCLE_PAYMENT'; // hard-coding this because we'll handle top-off in a different sequence of steps

  /**
   * send formatted data from the wizard steps to the parent to complete
   * the "run machine" and device transaction simulation
   */
  const submitMachineDrivenSimulationData = () => {
    const dataToSubmit = {
      deviceId: device.id,
      machineId: device.machineId,
      transactionType: selectedTransactionType,
      transactionAmount: Number(selectedTransactionAmountInDollars * 100),
      paymentStatus: selectedPaymentStatus,
    };

    return onSubmit(dataToSubmit);
  };

  /**
   * send formatted data from the wizard steps to the parent to complete
   * the "run machine" and device transaction simulation for live link transactions
   */
  const submitLiveLinkSimulationData = () => {
    const dataToSubmit = {
      deviceId: device.id,
      machineId: device.machineId,
      transactionAmount: Number(selectedTransactionAmountInDollars * 100),
      centsCustomerId: selectedCustomerId,
      storeId: device.storeId,
      businessId: device.businessId,
    };

    return onSubmit(dataToSubmit);
  };

  /**
   * navigate backwards in the wizard based on current progress in the wizard
   */
  const handleOnBackFunctionality = () => {
    switch (wizardStep) {
      case selfServiceDeviceTransactionScreens.ORIGINATION_SIMULATION:
        return onBack();
      case selfServiceDeviceTransactionScreens.CUSTOMER_SELECTION:
        setSelectedOriginationPoint(null);
        setSelectedCustomerDisplay(null);
        setSelectedCustomerId(null);
        return setWizardStep(selfServiceDeviceTransactionScreens.ORIGINATION_SIMULATION);
      case selfServiceDeviceTransactionScreens.TRANSACTION_TYPE:
        setSelectedOriginationPoint(null);
        return setWizardStep(selfServiceDeviceTransactionScreens.ORIGINATION_SIMULATION);
      case selfServiceDeviceTransactionScreens.TRANSACTION_AMOUNT:
        setSelectedTransactionType(null);
        if (selectedOriginationPoint === 'MACHINE_DRIVEN') {
          return setWizardStep(selfServiceDeviceTransactionScreens.TRANSACTION_TYPE);
        } else {
          return setWizardStep(selfServiceDeviceTransactionScreens.CUSTOMER_SELECTION);
        }
      case selfServiceDeviceTransactionScreens.FINAL_REVIEW:
        setSelectedTransactionAmountInDollars(null);
        return setWizardStep(selfServiceDeviceTransactionScreens.TRANSACTION_AMOUNT);
      default:
        return onBack();
    }
  };

  /**
   * determine the logic for disabling the final submit button
   */
  const disableFinalSubmitButton = () => {
    if (selectedOriginationPoint === 'MACHINE_DRIVEN') {
      return !selectedOriginationPoint || !selectedTransactionType || !selectedTransactionAmountInDollars;
    }

    return !selectedOriginationPoint || !selectedCustomerId || !selectedTransactionAmountInDollars;
  };

  /**
   * if the origination point is MACHINE_DRIVEN, call the machine-driven submit function.
   * 
   * if not, call the live link submit function
   */
  const handleSubmitForFinalReviewScreen = () => {
    if (selectedOriginationPoint === 'MACHINE_DRIVEN') {
      return submitMachineDrivenSimulationData();
    } else {
      return submitLiveLinkSimulationData();
    }
  }

  /**
   * render the final review screen for user to review their submissions
   */
  const renderFinalReviewScreen = () => {
    return (
      <DeviceTransactionFinalReviewScreen
        deviceId={device.id}
        machineId={device.machineId}
        selectedTransactionAmount={selectedTransactionAmountInDollars}
        selectedTransactionType={selectedTransactionType}
        selectedPaymentStatus={selectedPaymentStatus}
        selectedOriginationPoint={selectedOriginationPoint}
        selectedCustomerDisplay={selectedCustomerDisplay}
        onBack={handleOnBackFunctionality}
        onNext={handleSubmitForFinalReviewScreen}
        disableFinalSubmitButton={disableFinalSubmitButton}
      />
    );
  };

  /**
   * navigate forwards to the final review screen once transaction amount is identified
   */
  const handleOnNextForTransactionAmountSelection = () => {
    setWizardStep(selfServiceDeviceTransactionScreens.FINAL_REVIEW);
  };

  /**
   * render the transaction amount input
   */
  const renderTransactionAmountSelection = () => {
    return (
      <TransactionAmountSelection
        onBack={handleOnBackFunctionality}
        onNext={handleOnNextForTransactionAmountSelection}
        selectedTransactionAmountInDollars={selectedTransactionAmountInDollars}
        setSelectedTransactionAmount={(amount) => {
          setSelectedTransactionAmountInDollars(Number(amount));
        }}
      />
    );
  };

  /**
   * set the customer ID and display name selections for the customer assigned to the turn
   * 
   * @param {object} userData 
   */
  const setValuesForCustomerSelection = (customerData) => {
    setSelectedCustomerId(customerData.id);
    setSelectedCustomerDisplay(`${customerData.firstName} ${customerData.lastName}`);
  }

  /**
   * Navigate forward in the wizard to the transaction amount form
   */
  const handleOnNextForCustomerSelection = () => {
    setWizardStep(selfServiceDeviceTransactionScreens.TRANSACTION_AMOUNT);
  }

  /**
   * Render the screen that allows you to enter the technician name
   */
  const renderCustomerSelection = () => {
    return (
      <CustomerSelection
        onBack={handleOnBackFunctionality}
        onNext={handleOnNextForCustomerSelection}
        selectedCustomerDisplay={selectedCustomerDisplay}
        selectedCustomerId={selectedCustomerId}
        onValueChange={setValuesForCustomerSelection}
        customers={customers}
      />
    );
  }

  /**
   * move forward to the transaction amount selection process
   */
  const handleOnNextForTransactionTypeSelection = () => {    
    setWizardStep(selfServiceDeviceTransactionScreens.TRANSACTION_AMOUNT);
  };

  /**
   * render the UI to allow one to select transaction type
   */
  const renderTransactionTypeSelection = () => {
    const transactionTypes = ['COIN', 'EMV'];

    return (
      <TransactionTypeSelection
        transactionTypes={transactionTypes}
        selectedTransactionType={selectedTransactionType}
        onBack={handleOnBackFunctionality}
        onNext={handleOnNextForTransactionTypeSelection}
        onValueChange={setSelectedTransactionType}
      />
    );
  };

  /**
   * Determine which series of steps to show based on the origination point selection
   */
  const handleOnNextForOriginationPointSelection = () => {
    if (selectedOriginationPoint === 'MACHINE_DRIVEN') {
      setWizardStep(selfServiceDeviceTransactionScreens.TRANSACTION_TYPE);
    } else {
      setWizardStep(selfServiceDeviceTransactionScreens.CUSTOMER_SELECTION);
    }
  };

  /**
   * render the self-service transaction origination point selection
   */
  const renderOriginationPointSelection = () => {
    return (
      <SelfServiceOriginationPointSelection
        selectedOriginationPoint={selectedOriginationPoint}
        onBack={handleOnBackFunctionality}
        onNext={handleOnNextForOriginationPointSelection}
        onValueChange={setSelectedOriginationPoint}
      />
    );
  };

  /**
   * Determine the proper form/wizard step based on state of the component
   */
  const renderSelfServiceTransactionWizardSteps = () => {
    switch (wizardStep) {
      case selfServiceDeviceTransactionScreens.ORIGINATION_SIMULATION:
        return renderOriginationPointSelection();
      case selfServiceDeviceTransactionScreens.TRANSACTION_TYPE:
        return renderTransactionTypeSelection();
      case selfServiceDeviceTransactionScreens.CUSTOMER_SELECTION:
        return renderCustomerSelection();
      case selfServiceDeviceTransactionScreens.TRANSACTION_AMOUNT:
        return renderTransactionAmountSelection();
      case selfServiceDeviceTransactionScreens.FINAL_REVIEW:
        return renderFinalReviewScreen();
      default:
        return renderOriginationPointSelection();
    }
  };

  return <>{renderSelfServiceTransactionWizardSteps()}</>;
};

export default SelfServiceDeviceTransactionSimulator;
